import _ from 'lodash';
import { createSelector, lruMemoize } from 'reselect';
import { DateTime } from 'luxon';
import { CATEGORY_TYPES, KNOWLEDGE_TO_BE_EXPIRED_ITEMS_IN_DAYS } from '../../constants';
import { getParentIdByPath } from '../../utils';
import { isExternalSyncReadOnly } from '../../utils/knowledge.utils';
import workspaceService from '../../services/workspace.service';
import { getUser } from './user.selector';
import { hasPermissionsToEditItem } from './iam.selector';
import { findContactInSlices } from './contact.selector';

export const UNASSIGNED = 'unassigned';

export const getByIdCategory = (category, idToSearch) => {
  if (category.children && category.children.length !== 0) {
    let result;
    category.children.forEach(item => {
      if (result) return false;
      let res = getByIdCategory(item, idToSearch);

      if (res) result = res;
    });

    return result;
  }

  if (category.knowledge_item_medias && category.knowledge_item_medias !== 0) {
    return category.knowledge_item_medias.find(({ id }) => idToSearch === id);
  }
};

export const getKnowledgeItemById = ({ knowledge }, id) => {
  return getItemById(knowledge, { id });
};

// Memoized: the contact lookup builds a fresh { ...user } for user-editors, which
// tripped react-redux's stabilityCheck. Keyed on the state slices + resolved id so
// a repeat call with unchanged state returns the same reference.
export const getLastEditedBy = createSelector(
  [
    ({ contacts }) => contacts?.contactsById,
    ({ usersManagement }) => usersManagement?.list,
    (_state, knowledge) =>
      knowledge?.revisionLog?.length ? knowledge.revisionLog[0].userId : knowledge?.updatedBy,
  ],
  findContactInSlices,
);

export const getRoot = ({ categories }, initialWorkspaceId = null) => {
  //support orgs with and without workspaces
  if (!workspaceService.hasWorkspaces()) {
    if (!categories) return {};
    return categories.find(category => category.path?.toLowerCase() === 'root');
  }
  let workspaceId = initialWorkspaceId;

  if (!workspaceId) {
    workspaceId = workspaceService.getSelectedWorkspaceId();
  }

  if (!categories || !workspaceId) return {};
  return (
    categories.find(
      category => category.path?.toLowerCase() === 'root' && category.workspaceId === workspaceId,
    ) ?? {}
  );
};

export const getCategoriesWithLinkedItem = ({ itemsById }, id) => {
  return itemsById[id]?.categoriesInfo?.map(({ categoryId }) => categoryId) || [];
};

export const getCategoriesByPath = ({ categories, categoriesById }, path) => {
  if (_.isNull(categories)) return null;

  let parentId = getParentIdByPath(path);

  return getCategoriesById({ categoriesById }, parentId);
};

export const getCategoriesById = ({ categoriesById }, id) => {
  if (_.isNull(categoriesById)) return null;

  if (!categoriesById[id]) return null;

  return categoriesById[id]?.subcategories.map(i => getCategoryById({ categoriesById }, { id: i }));
};

export const getCategoryById = ({ categoriesById }, { id }) => {
  return !_.isNull(categoriesById) ? (categoriesById[id] ?? {}) : {};
};

// Call sites build `ids` inline (`[id]`, `sections.map(...)`), so a fresh array every
// render would defeat identity-based memoization — compare arrays by content instead.
const shallowArrayEqual = (a, b) =>
  a === b ||
  (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]));

const externalSyncDescendantMemoOptions = {
  equalityCheck: shallowArrayEqual,
  // One entry per mounted section (each passes its own ids/predicate).
  maxSize: 50,
};

/**
 * True when any category in the subtrees rooted at `ids` matches `predicate`.
 * Store nodes hold child ids in `subcategories` (not nested objects), so the walk
 * resolves them through `categoriesById`. Memoized on (categoriesById, ids,
 * predicate) so an unrelated store change doesn't re-walk the tree.
 */
const selectHasExternalSyncDescendant = createSelector(
  [
    ({ knowledge }) => knowledge?.categoriesById,
    (_state, ids) => ids,
    (_state, _ids, predicate) => predicate,
  ],
  (categoriesById, ids, predicate) => {
    // Mirrored subtrees make revisits real, and a cycle must not blow the stack.
    const visited = new Set();
    const matches = id => {
      if (visited.has(id)) return false;
      visited.add(id);
      const node = categoriesById?.[id];
      if (!node) return false;
      return predicate(node.options) || (node.subcategories ?? []).some(matches);
    };
    return (ids ?? []).some(matches);
  },
  {
    memoize: lruMemoize,
    memoizeOptions: externalSyncDescendantMemoOptions,
    argsMemoize: lruMemoize,
    argsMemoizeOptions: externalSyncDescendantMemoOptions,
  },
);

export const hasExternalSyncDescendantById =
  (ids, predicate = isExternalSyncReadOnly) =>
  state =>
    selectHasExternalSyncDescendant(state, ids, predicate);

export const getItemById = ({ itemsById }, { id }) => {
  return itemsById?.[id] || null;
};

export const getItemsByIds = ({ itemsById }, ids) => {
  return ids?.map(id => itemsById?.[id] || {}).filter(i => i?.id) || [];
};

export const generateBreadcrumbs = (
  { categoriesById, categories },
  { pathParts = [], initialWorkspaceId = null },
) => {
  const rootId = getRoot({ categories }, initialWorkspaceId)?.id;

  return pathParts
    ? pathParts
        .filter(part => part.toLowerCase() !== 'root' && part !== rootId)
        .map(id => categoriesById[id])
    : [];
};

export const getItemsByCategoryId = ({ itemsById, categoriesById }, { id }) => {
  const { knowledgeItems = [] } = !_.isNull(categoriesById) ? (categoriesById[id] ?? {}) : {};

  return knowledgeItems
    .map(({ id, linked, ownerIds = null, isSourceDerived, ...itemData }) => {
      const item = itemsById[id] || null;

      if (!!item && linked) {
        return {
          ...item,
          linked,
          linkOwnerIds: itemData.linkOwnerIds ?? ownerIds,
          isSourceDerived,
        };
      }
      if (!item) return null;
      return { ...item, isSourceDerived };
    })
    .filter(i => !!i);
};

export const getExpiredItems = ({ items }, { items: owners }) => {
  const expirationDate = DateTime.now().plus({
    days: KNOWLEDGE_TO_BE_EXPIRED_ITEMS_IN_DAYS,
  });

  const groupedItems = items
    ?.filter(({ expiresAt }) => expiresAt && expirationDate.diff(DateTime.fromISO(expiresAt)) > 0)
    .sort((a, b) => DateTime.fromISO(b.expiresAt).diff(DateTime.fromISO(a.expiresAt)))
    // create a map of ownerIds and array of items
    .reduce((acc, item) => {
      // Get valid owner IDs that exist in the owners list, or [UNASSIGNED] if none exist
      const ownerKeys = item.ownerIds?.filter(ownerId => owners[ownerId]).filter(Boolean) ?? [];
      const targetKeys = ownerKeys.length > 0 ? ownerKeys : [UNASSIGNED];

      // Add item to each target owner key
      return targetKeys.reduce(
        (acc, ownerKey) => ({
          ...acc,
          [ownerKey]: [...(acc[ownerKey] ?? []), item],
        }),
        acc,
      );
    }, {});

  // sort the map by number of items
  return Object.fromEntries(
    Object.entries(groupedItems || []).sort((a, b) => b[1].length - a[1].length),
  );
};

export const getKnowledgeItemsOpenEventsForOrganization = ({
  itemsById,
  itemsOpenEventsForOrganization,
}) => {
  return itemsOpenEventsForOrganization?.map(item => itemsById?.[item.id] || null).filter(i => !!i);
};

export const getKnowledgeItemsOpenEventsForUser = ({ itemsById, itemsOpenEventsForUser, id }) => {
  return itemsOpenEventsForUser?.map(item => itemsById?.[item.id] || null).filter(i => !!i);
};

/**
 * Check if the category is a section
 * Re-exported from category.utils.js for backward compatibility
 * if path contains root, sections are items that have an odd number of path parts
 * if path does not contain root, sections are items that have an even number of path partss
 * @param {*} pathParts
 * @returns boolean
 */
export { isSection } from '../../utils/category.utils';

export const getKnowledgeParentsByItemId = ({ categories }, { id }) => {
  return categories.filter(
    cat => cat.type === 'knowledge' && !!cat.knowledgeItems.find(item => item.id === id),
  );
};

export const getKnowledgeParentsByItemIdWithWorkspace = ({ categories }, workspace, { id }) => {
  if (!categories || !workspace?.list?.length) return { paths: [], existsInOnlyOneWorkspace: true };

  const parents = getKnowledgeParentsByItemId({ categories }, { id });

  const workspaceIds = new Set(parents.map(p => p.workspaceId));
  const pathWorkspaces = workspace.list.filter(ws => workspaceIds.has(ws.id));
  const workspaceById = new Map(pathWorkspaces.map(ws => [ws.id, ws]));

  const paths = parents
    .map(cat => ({
      ...cat,
      workspaceName: workspaceById.get(cat.workspaceId).name,
      linked: !!cat.knowledgeItems.find(item => item.id === id)?.linked,
    }))
    .sort((a, b) => a.workspaceName.localeCompare(b.workspaceName));

  const existsInOnlyOneWorkspace = workspaceIds.size === 1;

  return { paths, existsInOnlyOneWorkspace };
};

export const isKnowledgeReady = ({ knowledge }) => knowledge.isReady;

export const getItemCategories = ({ knowledge }, { id }) => {
  // categories is null until getKnowledgeContent() loads; return [] so callers
  // that .map() the result don't crash when the move dialog opens mid-load.
  return (
    knowledge.categories?.filter(cat => cat?.knowledgeItems?.find(item => item.id === id)) ?? []
  );
};

export const getItemCategory = createSelector(
  [({ knowledge }) => knowledge.categories, (_state, { id }) => id],
  (categories, id) =>
    categories?.find(cat => cat?.knowledgeItems?.find(item => item.id === id && !item.linked)),
);

export const getKeywordsByItemId = ({ itemsById }, { id }) => {
  return itemsById[id]?.keywords || [];
};

export const isParentSharedToHub =
  path =>
  ({ knowledge }) => {
    const { categoriesById } = knowledge;
    const id = getParentIdByPath(path);
    if (!id) return false;

    const parent = categoriesById[id];
    if (!parent) return false;

    return parent.shared?.isShared ?? false;
  };

/**
 * Check if the user is the owner of the knowledge item before action on it and its children
 *
 * @param {ReduxState} state
 * @param {{ id: ObjectId }} { id }
 * @return {Boolean} if the user is the owner of the knowledge item and its children
 */
export function isKnowledgeOwner(state, { id }) {
  if (!id) return false;

  const hasPermissions = hasPermissionsToEditItem(state);

  // Check if the user has permission to bypass the owner restriction
  if (hasPermissions) return true;

  // Get user id
  const { id: userId } = getUser(state);

  // check children ownership
  return checkChildrenOwnership(state, id, userId);
}

/**
 * Check if the user is the owner of the category and all of its children
 * @param {ReduxState} State
 * @param {ObjectId} id
 * @param {ObjectId} userId
 * @returns {Boolean} if the user is the owner of the category and all of its children
 */
function checkChildrenOwnership(state, id, userId) {
  // get category by id
  const item = getCategoryById(state.knowledge, { id });

  // check if the category is owned by the user
  if (item.ownerIds && !item.ownerIds.includes(userId)) {
    return false;
  }

  if (item.type === CATEGORY_TYPES.CATEGORY) {
    // recursively check if all of the subcategories are owned by the user
    return item.subcategories.every(subcategoryId => {
      return checkChildrenOwnership(state, subcategoryId, userId);
    });
  }

  return item.knowledgeItems.every(({ id, linked, ownerIds: linkOwnerIds }) => {
    // Check if the knowledge item is linked
    if (linked) {
      return linkOwnerIds.includes(userId);
    }

    const knowledgeItem = getItemById(state.knowledge, { id });
    if (!knowledgeItem) return true;

    return knowledgeItem?.ownerIds.includes(userId);
  });
}

/**
 * Check if the user have permissions to edit the item
 *
 * @param {ReduxState} state
 * @param {{ ownerIds: string[], linkOwnerIds: string[], linked: boolean, ownerIds: string[] }}
 * @return {boolean} if the user have permissions to edit the item
 */
export function haveEditPermissions(state, { linkOwnerIds, linked, ownerIds }) {
  const hasPermissions = hasPermissionsToEditItem(state);

  if (hasPermissions) return true;

  const { id: userId } = getUser(state);

  if (linked) {
    return linkOwnerIds.includes(userId);
  }

  if (ownerIds && ownerIds.length > 0) return ownerIds.includes(userId);

  return false;
}

/**
 * Get all binders (knowledge type categories) that have at least one item
 * Used for filter dropdowns to show only relevant categories
 *
 * @param {{ knowledge: { categories: Array } }} state
 * @returns {Array<{ id: string, title: string, path: string }>} Array of categories with items
 */
export const getCategoriesWithItems = ({ knowledge }) => {
  const { categories } = knowledge;

  if (!categories) return [];

  return categories
    .filter(
      category =>
        category.type === CATEGORY_TYPES.KNOWLEDGE &&
        category.knowledgeItems &&
        category.knowledgeItems.length > 0,
    )
    .map(({ id, title, path }) => ({ id, title, path }));
};

/**
 * Get all knowledge item IDs under a category recursively
 * This includes items from all subcategories
 *
 * @param {{ categoriesById: Object }} knowledge state
 * @param {Object} categoriesById - The category mapping by ID
 * @param {string} categoryId - The category ID to start from
 * @returns {string[]} Array of knowledge item IDs
 */
export const getAllKnowledgeItemIdsUnderCategory = ({ categoriesById, categoryId }) => {
  const itemIds = new Set();

  const collectItems = catId => {
    const category = categoriesById?.[catId];
    if (!category) return;

    category.knowledgeItems?.forEach(({ id }) => itemIds.add(id));
    category.subcategories?.forEach(subId => collectItems(subId));
  };

  collectItems(categoryId);
  return Array.from(itemIds);
};
